import { request } from '@/common';
import { HelperCore } from '@/common/helper';
import userStore from '@/store/user';
import { MultiColumn } from '@/utils/multi-column';
import { type IMultColumnResult } from '@q/solib-utils/dist/multi-column';
import { cloneDeep, merge } from 'lodash-es';
import { makeAutoObservable, runInAction } from 'mobx';
import stateStore from '../square-video/state-store';
import { Item } from '../square-video/store';

export const aiToolsMap = {
  expand: {
    video: 'https://s0.ssl.qhres2.com/static/23646106c52b0765.webm',
    image: 'https://p5.ssl.qhimg.com/t019b2c25eb983bd1a2.png',
    label: '扩图',
    videoBG: true,
    key: 'expand',
    path: '/generate/image/expand',
  },
  // expand: {
  //   video: 'https://s3.ssl.qhres2.com/static/c33941b61da88150.webm',
  //   image: 'https://p4.ssl.qhimg.com/t011719473458033ed5.png',
  //   label: '智能抠图',
  //   key: 'expand',
  //   link: 'https://pic.360.com/tools/image-editor/removebg?subpage=tools_removebg_21&ver=3&src=nami_image&srcg=nami_bot'
  // },
  erase: {
    video: 'https://s3.ssl.qhres2.com/static/825ff8a2970e92f2.webm',
    image: 'https://p1.ssl.qhimg.com/t012a23c60d338b530f.png',
    label: '擦除',
    key: 'erase',
    path: '/generate/image/erase',
  },
  repaint: {
    video: 'https://s1.ssl.qhres2.com/static/ae97539d5e7944d9.webm',
    image: 'https://p3.ssl.qhimg.com/t110b9a93017a0da314519c2060.png',
    label: '区域重绘',
    key: 'repaint',
    path: '/generate/image/repaint',
  },
  sharpen: {
    video: 'https://s0.ssl.qhres2.com/static/ae722ec4860b9600.webm',
    image: 'https://p2.ssl.qhimg.com/t01eede7553baa96250.png',
    label: '变清晰',
    key: 'sharpen',
    link: 'https://pic.360.com/tools/image-editor/beautify?subpage=tools_beautify_10&ver=3&src=nami_image&srcg=nami_bot',
    path: '/generate/image/sharpen',

  },
  matting: {
    video: 'https://s3.ssl.qhres2.com/static/c33941b61da88150.webm',
    image: '	https://p4.ssl.qhimg.com/t011719473458033ed5.png',
    label: 'AI抠图',
    key: 'matting',
    link: 'https://pic.360.com/tools/image-editor/beautify?subpage=tools_beautify_10&ver=3&src=nami_image&srcg=nami_bot',
    path: '/generate/image/matting',

  },
  // resize: {
  //   video: 'https://s3.ssl.qhres2.com/static/aa97d5572bae184e.webm',
  //   image: 'https://p5.ssl.qhimg.com/t019b2c25eb983bd1a2.png',
  //   label: '无损改尺寸',
  //   key: 'resize',
  //   link: 'https://pic.360.com/tools/image-editor/enlarge?subpage=tools_enlarge_20&src=nami_image&srcg=nami_bot',
  //   path: '/generate/image/resize',
  // },
  // batch: {
  //   video: '',
  //   image: 'https://p4.ssl.qhimg.com/dmt/322_182_/t01915575a37f4f4425.png',
  //   label: '批量编辑',
  //   key: 'batch',
  //   link: 'https://pic.360.com/tools/image-editor/replacebg?subpage=tools_replacebg_28&ver=3&src=nami_image&srcg=nami_bot',
  //   path: '/generate/image/batch',

  // },
}

class Store {
  constructor() {
    makeAutoObservable(this);
  }

  list: Item[] = [];
  renderList: IMultColumnResult[] = [];
  unique: string[] = [];
  loading = false;
  ended = false;
  lastScrollTop = 0;
  columns = new MultiColumn(278, 8, document.documentElement.clientWidth - 228);

  calcColumns(data: Item[]) {
    const renderList = cloneDeep(this.renderList);
    const result = this.columns.calcColumns(data.map((item) => item.exDataJSON));
    result.forEach((col) => {
      renderList.push(col);
    });
    this.renderList = renderList;
  }

  renewColumns() {
    this.columns.renewColumns();
    this.renderList = [];
    this.calcColumns(this.list);
  }

  updateItem(id: string, params: Record<string, any>) {
    const index = this.list.findIndex((item) => item.id === id);
    if (index !== -1) {
      this.list[index] = merge(this.list[index], params);
    }
  }

  getMList({
    action = 1,
    crec,
  }: {
    /** 1：下拉，2：上拉（或整体刷新） */
    action?: 1 | 2;
    /** crec=1 非个性化crec=0 个性化   */
    crec?: 0 | 1;
  } = {}) {
    if (this.loading) return;
    if (action === 2) {
      runInAction(() => {
        this.list = [];
        this.unique = [];
        this.loading = false;
        this.ended = false;
        this.columns.renewColumns();
        this.renderList = [];
        this.loadData({ action, crec });
      });
    } else {
      this.loadData({ action, crec });
    }
  }

  private async loadData({
    action,
    crec,
  }: {
    /** 1：下拉，2：上拉（或整体刷新） */
    action?: 1 | 2;
    /** crec=1 非个性化crec=0 个性化   */
    crec?: 0 | 1;
  }) {
    if (this.ended) return;
    runInAction(() => {
      this.loading = true;
    });

    try {
      // https://geelib.qihoo.net/geelib/knowledgeShare?shorturl=ebdc4ea9b979afa740abe24e3d
      const { data } = await request.get<{
        res: Item[];
      }>('https://papi.look.360.cn/mlist', {
        params: {
          sign: '360_87cadf5d',
          scene: '299',
          uid: userStore.uid,
          c: HelperCore.IS_ONLINE ? 'namigallery_pc' : 'namigallery_pc_test',
          f: 'json',
          /** 用户设备标识，0为安卓，1为iOS，2为PC */
          device: 2,
          action,
          hsid: stateStore.hsid,
          crec,
          n: 20,
        },
      });

      const resList = [];
      const unique = cloneDeep(this.unique);

      for (const item of data.res) {
        if (unique.includes(item.id)) {
          continue;
        } else {
          unique.push(item.id);
        }

        try {
          const rss = JSON.parse(item.rss_ext);
          rss.image_list = rss.image_list.filter((i) => !!i.url_download);
          if (!rss.image_list.length) {
            continue;
          }
          let width = 0;
          let height = 0;
          if (rss.image_list[0].ratio) {
            const [w, h] = rss.image_list[0].ratio.split(':');
            const ratio = +w / +h;
            width = 1024;
            height = 1024 / ratio;
          } else {
            const img = await HelperCore.getImageSize(rss.image_list[0].url_download);
            width = img.width;
            height = img.height;
          }
          resList.push({
            ...item,
            index: -1,
            lv_imgs: rss.image_list.map((i) => i.url_download),
            exDataJSON: {
              ...item.exDataJSON,
              width,
              height,
            },
            rssData: {
              ...rss,
              mtype: rss.image_list[0].biz_type,
              mid: rss.image_list[0].mid,
              create_time: rss.create_time,
            },
          });
        } catch (e) {
        }
      }

      runInAction(() => {
        this.unique = unique;
      });

      const mRes = await request.post<any, {
        data: {
          avatar: string;
          data_id_str: string;
          nick_name: string;
          like_click: 0 | 1;
          liked_works_count: number;
        }[];
      }>('/media/user_info?appsource=so&device_platform=Web', {
        mids: resList.map((item) => ({
          data_id_str: item.rssData.data_id,
          mid: item.rssData.mid,
          mtype: item.rssData.mtype,
          qid: item.zmt.id,
        })),
      }, {
        dataIsJson: true,
      });

      mRes.data.forEach((mItem) => {
        const index = resList.findIndex(({ rssData }) => rssData.data_id === mItem.data_id_str);
        if (index !== -1) {
          resList[index].exDataJSON.avatar = mItem.avatar || '';
          resList[index].exDataJSON.nickname = mItem.nick_name || resList[index].zmt.name;
          resList[index].exDataJSON.islike = mItem.like_click;
          resList[index].exDataJSON.likenum = mItem.liked_works_count;
        }
      });

      resList.forEach((item, index) => {
        item.index = this.list.length + index;
      });

      runInAction(() => {
        this.list.push(
          ...resList,
        );
        this.calcColumns(resList);
        if (!resList.length || !mRes.data.length) {
          this.ended = true;
        }
      });
    } catch { }
    runInAction(() => {
      this.loading = false;
    });
  }
}

export default new Store();
